import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Hourglass, ChevronDown } from "lucide-react";
import { api } from "../../lib/api";
import { dateBR, dateTimeBR } from "../../lib/format";
import { Button } from "../ui/button";

const ST = { done: [CheckCircle2, "text-emerald-600", "CONCLUÍDA"], processing: [Circle, "text-indigo-600", "EM PROCESSAMENTO"], waiting: [Hourglass, "text-amber-600", "PRECISAMOS DE VOCÊ"] };

function Item({ it, onOpen }) {
  const [Icon, cls, label] = ST[it.state];
  return (
    <div className="flex items-center gap-3 py-2.5" data-testid={`client-batch-item-${it.id}`}>
      <Icon className={`h-4 w-4 shrink-0 ${cls}`} />
      <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{it.label} — {it.employee_name || it.title}{it.new && <span className="ml-2 rounded bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5" data-testid={`client-item-new-${it.id}`}>NOVO</span>}</div>
        <div className={`text-[11px] font-semibold ${cls}`} data-testid={`client-item-state-${it.id}`}>{label}</div></div>
      {it.kind === "request" && (it.state === "done"
        ? <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onOpen(it.id)} data-testid={`client-item-result-${it.id}`}>VER RESULTADO</Button>
        : <Button size="sm" variant="ghost" onClick={() => onOpen(it.id)} data-testid={`client-item-open-${it.id}`}>Ver</Button>)}
    </div>);
}

function BatchBox({ b, onOpen }) {
  const [open, setOpen] = useState(b.status !== "finalizado" || b.new_results > 0);
  const all = b.total > 0 && b.done === b.total;
  return (
    <div className="card-surface p-5 space-y-3" data-testid={`client-batch-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><div className="font-display text-lg font-semibold">{b.title}</div>
          <div className="text-xs text-slate-500">{b.total} solicitação(ões) enviada(s) · <span className="font-mono">{b.protocol}</span>{b.promised_due_date && !all ? ` · previsão de retorno até ${dateBR(b.promised_due_date)}` : ""}</div></div>
        <div className="flex items-center gap-3">
          {b.new_results > 0 && <span className="rounded-full bg-blue-600 text-white text-xs font-bold px-3 py-1" data-testid={`client-batch-new-${b.id}`}>{b.new_results} {b.new_results === 1 ? "RESULTADO DISPONÍVEL" : "RESULTADOS DISPONÍVEIS"}</span>}
          <div className="text-right"><div className="font-display text-2xl font-semibold" data-testid={`client-batch-progress-${b.id}`}>{b.progress}%</div><div className="text-xs text-slate-500" data-testid={`client-batch-count-${b.id}`}>{b.done} de {b.total} concluídas</div></div>
        </div>
      </div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full transition-all duration-500 ${all ? "bg-emerald-500" : "bg-indigo-600"}`} style={{ width: `${b.progress}%` }} /></div>
      {all && <div className="text-sm font-semibold text-emerald-700" data-testid={`client-batch-all-done-${b.id}`}>✓ TODAS AS SOLICITAÇÕES FORAM CONCLUÍDAS{b.finalized_at ? ` · ${dateTimeBR(b.finalized_at)}` : ""}</div>}
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-slate-900" data-testid={`client-batch-toggle-${b.id}`}>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />Suas solicitações</button>
      {open && <div className="divide-y">{b.items.map((it) => <Item key={it.id} it={it} onOpen={onOpen} />)}</div>}
    </div>);
}

export function ClientResults({ onOpen, refreshKey }) {
  const [bs, setBs] = useState([]);
  useEffect(() => { api.get("/client/batches").then((r) => setBs(r.data)).catch(() => setBs([])); }, [refreshKey]);
  if (!bs.length) return null;
  return (
    <section className="space-y-3 mb-8" data-testid="client-results">
      <div className="label-xs">Processamento das suas solicitações</div>
      {bs.map((b) => <BatchBox key={b.id} b={b} onOpen={onOpen} />)}
    </section>);
}
